// ============================================================================
// ui/product-detail-bar.js — the gate's bottom-centre product plate
// ============================================================================
// Ryan's spec (2026-07-15): "in the bottom center of the display (desktop), when
// you mouseover a product option, we use that available space down there to write
// out the blurb of the product... above the description we could have the brand
// name, and to the left a logo for the brand."
//
// It replaces the pills' pop-up tooltips as the VISUAL surface. The reason isn't
// that a tooltip is too small — it's that a tooltip can't carry a mark, and once
// UMAX / Troy Barbell sit alongside the in-house line, whose a product is IS the
// information. See product-info.js for the data shape that makes that a record
// rather than a rewrite.
//
// The space is real, not borrowed: measured at beat 2, the lowest panel ends at
// y=761 and the actions row runs 845-888 with Back pinned left and the CTA pinned
// right. The centre of that row — ~860px of it — is empty. So the bar is absolutely
// centred INSIDE that row: it can't be shoved off-centre by the buttons' widths,
// and it costs no layout because the row already exists.
//
// SELECTION DRIVES IT, NOT JUST HOVER — this is the load-bearing bit.
// A hover-only bar would be invisible on touch, and Ryan's own laptop reports a
// coarse pointer, so it'd be unreliable even on his desktop. The gate already made
// this exact call once: beat 2 is click-then-Next rather than hover-to-expand for
// precisely this reason (see select-weights-step.js's header). Tapping a pill
// selects it, which writes it here — so the bar works with no pointer at all.
//
// NO RESTING STATE, AND IT LINGERS ON THE WAY OUT.
//   nothing pointed at yet = hidden entirely
//   hover / focus / select = write that product
//   leaving a pill         = hold, then fade out LINGER_MS later
//   entering another       = cancel the fade, swap the words
//
// The first cut had a resting state — the brand plate alone, so the space read as
// branded rather than empty. Ryan killed it, and both his reasons were the same
// bug: "not a huge fan of the constant logo with 'Ballast Strength' at the bottom"
// and "when you do mouseover a pill, the BALLAST STRENGTH changes positions/gets
// smaller to accommodate the paragraph text — makes for a lot of quick jumps as
// you mouse from one pill to the next." Resting was a SECOND layout (bigger brand,
// no blurb), so every enter AND leave re-laid the plate out: eight re-layouts
// across a 4-pill panel. Now there's one layout, entered once, and after that only
// the words change.
//
// The linger is Ryan's too, and it's what makes the no-resting-state version work:
// "when your mouse is no longer on a particular product's pill, the description
// fades away after 2-3 seconds — that way it's not constantly flickering on and
// off as you move from one product to the next, but once you're legitimately done
// mousing over products, it'll still disappear."
// It's hysteresis, and it beats both alternatives. Hiding on leave flickers, since
// crossing from one pill to its neighbour leaves a gap of milliseconds. Never
// hiding (the previous cut) doesn't flicker but leaves a description of something
// you stopped looking at sitting there indefinitely. A grace period longer than
// any pill-to-pill traverse and shorter than genuine disinterest gets both: moving
// between pills, the timer never fires at all.
//
// DESKTOP ONLY for now (Ryan: "park [mobile] until desktop is right") — the CSS
// hides it below the gate's mobile breakpoint, where there's no bottom-centre
// strip to speak of. The pills keep their own screen-reader descriptions either
// way, so nothing is lost when it's hidden.
//
// `aria-hidden` on purpose: every line it can show is already on the pill it came
// from, via aria-describedby. Announcing it here as well would read the same
// sentence to a screen-reader user twice.
// ============================================================================

import { h } from './dom.js';
import { infoFor } from './product-info.js';

const SWAP_MS = 130;    // word-to-word, once the plate is already up
const REVEAL_MS = 260;  // the plate arriving from nothing — twice the swap, it's a bigger event
const FADE_OUT_MS = 320;
// Tuned by feel, not by theory: Ryan asked for "2-3 seconds", then "a fraction
// sooner", then "a little bit quicker" — so this is now deliberately BELOW the
// range he first named, because 2s read as sluggish once he had it in front of him.
// The only hard requirement is that it comfortably clear a pill-to-pill move, and
// those are single-frame; 1.5s has ~1.4s of headroom. Don't go under ~1s — that
// starts catching a slow hand crossing the gap between two pills, and the flicker
// this whole mechanism exists to kill comes straight back.
const LINGER_MS = 1500;

/** Match the rest of the gate: reduced motion means no fades, not slower fades. */
const prefersReduced = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {{state: object}} ctx
 * @returns {{el: HTMLElement, peek: Function, sync: Function, setVisible: Function}}
 */
export function createProductDetailBar(ctx) {
  const logo = h('div', { class: 'wd-pdb__logo' });
  const brandEl = h('span', { class: 'wd-pdb__brand' });
  const blurbEl = h('span', { class: 'wd-pdb__blurb' });
  const text = h('div', { class: 'wd-pdb__text' }, brandEl, blurbEl);
  const el = h('div', { class: 'wd-pdb', 'aria-hidden': 'true', hidden: true }, logo, text);

  let armed = false;    // beat 2 is up — allowed to show itself once it has content
  let painted = '';     // what's on screen, so an identical repaint doesn't re-fade
  let swap = null;
  let linger = null;    // the pending fade-out; alive only between leave and timeout
  let fadingOut = false; // `swap` is the fade-out, not a word swap — see fill()

  /** Newest pick first: `selectedProducts` is append-ordered, so the last entry is
   *  the one the buyer just chose — the one they're still thinking about. */
  function lastPicked() {
    const list = ctx.state.ui.selectedProducts;
    return Array.isArray(list) && list.length ? list[list.length - 1] : null;
  }

  /** Wipe the plate and forget it, so the next fill arrives fresh. */
  function clear() {
    el.hidden = true;
    painted = '';
    swap?.cancel();
    swap = null;
    fadingOut = false;
  }

  /** Cancel the pending fade-out AND one already in flight. Both are "it's leaving";
   *  re-entering has to stop it whichever stage it's reached, or a pill re-entered
   *  during the 320ms fade would keep right on fading to nothing. */
  function stopLeaving() {
    if (linger) { clearTimeout(linger); linger = null; }
    if (fadingOut) {
      swap?.cancel(); // cancel drops the forwards-fill, so opacity snaps back to 1
      swap = null;
      fadingOut = false;
    }
  }

  /** Leave/blur: hold what's showing, then fade it out unless something re-enters. */
  function startLeaving() {
    stopLeaving();
    if (el.hidden || !armed) return;
    linger = setTimeout(() => {
      linger = null;
      swap?.cancel();
      if (prefersReduced()) { clear(); return; }
      // Fades the WHOLE plate, mark included — it's leaving, not being rewritten.
      const out = el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: FADE_OUT_MS, easing: 'ease-in', fill: 'forwards' });
      swap = out;
      fadingOut = true;
      out.onfinish = () => { if (swap === out) clear(); };
    }, LINGER_MS);
  }

  /**
   * Write a product in. Never clears: called with nothing to say, it leaves the
   * last thing standing rather than emptying (see the header).
   * @param {?{category?: string, categoryId?: string, kind: string}} target
   */
  function fill(target) {
    // Any fill is a re-entry, so the plate stops leaving — and this MUST come
    // before the `painted` early-return below. Walking back onto the pill you just
    // left is the identical product, so it early-returns; if the cancel lived after
    // that check, the plate would keep fading out from under a pill you're pointing
    // straight at.
    stopLeaving();
    if (!armed || !target) return;
    const info = infoFor(target.category ?? target.categoryId, target.kind);
    // No copy written for this product yet — a new SKU lands in CATEGORIES before
    // its blurb does. Hold what's there rather than flashing a half-empty plate.
    if (!info) return;

    const key = `${info.brand.id}|${info.blurb}`;
    if (key === painted) return;
    painted = key;

    brandEl.textContent = info.brand.name;
    blurbEl.textContent = info.blurb;

    // The placeholder plate is the `logo:null` case, and it's what ships today.
    // Swapping in real art is this one branch — nothing else moves.
    if (info.brand.logo) {
      logo.style.backgroundImage = `url("${info.brand.logo}")`;
      logo.classList.remove('is-placeholder');
    } else {
      logo.style.backgroundImage = '';
      logo.classList.add('is-placeholder');
    }

    const reveal = el.hidden;
    el.hidden = false;

    if (prefersReduced()) { swap?.cancel(); swap = null; return; }
    // A hard swap reads as a glitch when you sweep across four pills; a fade makes
    // it one surface being rewritten. Cancelled rather than queued, so a fast sweep
    // doesn't stack half-finished fades. The FIRST fill fades the whole plate in —
    // it's arriving, mark and all. After that only the WORDS fade, because the mark
    // and the box must not move; that was the jumpiness Ryan called out.
    swap?.cancel();
    swap = (reveal ? el : text).animate(
      [{ opacity: 0 }, { opacity: 1 }],
      { duration: reveal ? REVEAL_MS : SWAP_MS, easing: 'ease-out', fill: 'backwards' },
    );
  }

  return {
    el,
    /** Hover/focus a pill, or null on leave/blur — which starts the fade-out clock. */
    peek(categoryId, kind) {
      if (categoryId) fill({ categoryId, kind });
      else startLeaving();
    },
    /** Re-read selection — the gate calls this whenever a pill is toggled. */
    sync() { fill(lastPicked()); },
    /** Beat 2 only: there are no pills to describe on beat 1. */
    setVisible(on) {
      armed = on;
      if (on) { fill(lastPicked()); return; } // nothing picked yet = stays hidden
      // Back: hide and forget at once — no linger. The whole screen is leaving, so
      // a plate fading out on its own clock over the top of the reverse
      // choreography would be a second, competing animation.
      stopLeaving();
      clear();
    },
  };
}
